import React from 'react';
import PropTypes from 'prop-types';
import BillingProp from '../../propTypes/billingProp';
import Request from '../../../../services/request';
import Notifications from '../../../../services/notifications';
import { compose, isHigherAdmin } from '../../../../services/utilityFunctions';
import withCSRF from '../../../../commonComponents/withCSRF';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormInput from '../../../../commonComponents/forms/FormInput';
import FormActions from './FormActions';
import FormHeader from '../../../../commonComponents/forms/FormHeader';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import FormAddressFragment from '../../../../commonComponents/forms/Address/FormAddressFragment';
import UserProp from '../../propTypes/userProp';
import withLoggedUser from '../../../../commonComponents/withLoggedUser';
import Separator from '../../../../commonComponents/forms/Separator';
import TaxIdTypeProp from '../../../subscriptionDetails/propTypes/TaxIdTypeProp';
import BusinessTypeProp from '../../propTypes/businessTypeProp';

/**
 * Form to update the current logged user
 */
class BillingForm extends React.Component {
  constructor(props) {
    super(props);

    const { billing, fieldsRequired } = props;
    console.log('BillingForm -> billing', billing);
    const {
      billingAddress,
      invoiceEmail,
      businessType,
      taxId,
      taxCode,
      taxType,
      billingLanguage,
    } = billing;

    this.state = {
      firstName: billingAddress?.firstName ?? '',
      lastName: billingAddress?.lastName ?? '',
      companyName: billingAddress?.companyName ?? '',
      phone: billingAddress?.phone ?? '',
      email: invoiceEmail,
      billingLanguage,
      tax_info: {
        businessType,
        taxId,
        taxCode,
        taxType,
      },
      address: {
        address: billingAddress?.address ?? '',
        address_1: billingAddress?.address_1 ?? '',
        address_2: billingAddress?.address_2 ?? '',
        city: billingAddress?.city ?? '',
        zipCode: billingAddress?.zipCode ?? '',
        state: billingAddress?.state ?? '',
        country: billingAddress?.country ?? '',
        countryCode: billingAddress?.countryCode ?? '',
        provinceCode: billingAddress?.provinceCode ?? '',
        provinceCountryCode: billingAddress?.provinceCountryCode ?? '',
      },
    };

    this.validationRules = {
      firstName: { required: false },
      lastName: { required: false },
      companyName: { required: fieldsRequired },
      phone: { required: false },
      email: { required: fieldsRequired },
      billingLanguage: { required: fieldsRequired },
      businessType: { required: fieldsRequired },
      taxId: { required: false },
      taxCode: { required: fieldsRequired },
      address_1: { required: false },
      address_2: { required: false },
      city: { required: false },
      zipCode: { required: false },
      state: { required: false },
      countryCode: { required: fieldsRequired },
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleAddressChange = this.handleAddressChange.bind(this);
    this.createInput = this.createInput.bind(this);
  }

  handleSubmit() {
    const { t, _csrf, onBillingChange } = this.props;
    const params = {
      ...this.state,
    };

    new Request(_csrf)
      .put('/profile/billing/edit', params)
      .done(() => {
        Notifications.showNotificationSuccess(
          t('success'),
          t('success_update'),
        );
        onBillingChange(params);
      })
      .fail(err => {
        let error = t('error_profileUpdate');
        if (err && err.responseJSON) {
          error = err.responseJSON.message || t(err.responseJSON.error);
        }
        Notifications.showNotificationError(t('error'), error);
      });
  }

  handleChange(field) {
    return value => {
      this.setState({ [field]: value });
    };
  }

  handleAddressChange(newAddress) {
    this.setState(({ address }) => ({
      address: { ...address, ...newAddress },
    }));
  }

  handleSubSectionChange(section) {
    return field => value => {
      this.setState(
        state => {
          if (field === 'taxCode') {
            let { taxType } = state[section];
            const taxIdType = this.props.taxIdTypes.find(
              txId => txId.taxCode === value,
            );
            if (taxIdType) {
              ({ taxType } = taxIdType);
            }
            return {
              ...state,
              [section]: { ...state[section], [field]: value, taxType },
            };
          }
          return {
            ...state,
            [section]: { ...state[section], [field]: value },
          };
        },
        () => {},
      );
    };
  }

  createInput(section, field, disabled, typeParam) {
    const { t, fieldsRequired, taxIdTypes, businessTypes, languages } =
      this.props;

    const type = typeParam || 'text';

    let value;
    let changeHandler;
    if (section === 'main') {
      value = this.state[field];
      changeHandler = this.handleChange(field);
    } else {
      value = this.state[section][field];
      changeHandler = this.handleSubSectionChange(section)(field);
    }
    let placeholder = '';
    let inputProps = {};
    if (section === 'main') {
      if (field === 'billingLanguage') {
        inputProps = {
          required: fieldsRequired,
          items: languages.map(l => ({ id: l.code, text: l.name })),
        };
      }
    }
    if (section === 'tax_info') {
      if (field === 'businessType') {
        inputProps = {
          required: fieldsRequired,
          items: [
            { id: 'individual', text: t('billing_business_type_individual') },
            ...businessTypes.map(b => ({
              id: b.code,
              text: b.emoji ? `${b.emoji} ${b.name}` : b.name,
            })),
          ],
        };
      }
      if (field === 'taxCode') {
        inputProps = {
          required: fieldsRequired,
          items: taxIdTypes.map(c => ({
            id: c.taxCode,
            text:
              c.taxType === 'eu_vat'
                ? `${c.flagEmoji} ${c.countryName} VAT number`
                : `${c.flagEmoji} ${c.name}`,
          })),
        };
      }
      if (field === 'taxId') {
        const { taxCode } = this.state[section];
        if (taxCode) {
          const taxIdType = taxIdTypes.find(txId => txId.taxCode === taxCode);
          if (taxIdType) {
            ({ placeholder } = taxIdType);
          }
        }
      }
    }

    return (
      <FormInput
        disabled={disabled}
        id={field}
        name={field}
        value={value}
        onChange={changeHandler}
        label={t(`subscriptionCreate_billing_${field}`)}
        labelClassName="col-2"
        containerClassName="col-7"
        type={type}
        groupProps={{ className: 'row' }}
        inputProps={{ ...inputProps }}
        placeholder={placeholder}
      />
    );
  }

  render() {
    const { t, countries, isResellerInstance, loggedUser } = this.props;
    const { address } = this.state;
    const disabled = isResellerInstance && !isHigherAdmin(loggedUser);

    return (
      <Form
        id="billingForm"
        className="m-form m-form--fit m-form--label-align-right"
        submitMethod={this.handleSubmit}
        validationRules={this.validationRules}
      >
        {submitHandler => (
          <div className="m-portlet__body">
            <FormHeader title={`${t('billing_info')}`} />
            {this.createInput('main', 'firstName', disabled)}
            {this.createInput('main', 'lastName', disabled)}
            {this.createInput('main', 'companyName', disabled)}
            {this.createInput('main', 'phone', disabled)}
            {this.createInput('main', 'email', disabled, 'email')}
            {this.createInput('main', 'billingLanguage', disabled, 'select')}
            <Separator />
            {this.createInput('tax_info', 'businessType', disabled, 'select')}
            {this.createInput('tax_info', 'taxCode', disabled, 'select')}
            {this.createInput('tax_info', 'taxId', disabled)}
            <Separator />
            <FormAddressFragment
              disabled={disabled}
              style={{ paddingBottom: '30px' }}
              address={address}
              onAddressChange={this.handleAddressChange}
              onFieldChange={this.handleSubSectionChange('address')}
              countries={countries}
            />
            <FormActions submitHandler={submitHandler} />
          </div>
        )}
      </Form>
    );
  }
}

BillingForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  billing: PropTypes.shape(BillingProp.propType).isRequired,
  onBillingChange: PropTypes.func.isRequired,
  isResellerInstance: PropTypes.bool.isRequired,
  loggedUser: PropTypes.shape(UserProp.propType),
  taxIdTypes: PropTypes.arrayOf(PropTypes.shape(TaxIdTypeProp.propType))
    .isRequired,
  businessTypes: PropTypes.arrayOf(PropTypes.shape(BusinessTypeProp.propType))
    .isRequired,
  fieldsRequired: PropTypes.bool,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
};

BillingForm.defaultProps = {
  loggedUser: null,
  fieldsRequired: true,
};

export default compose(withCSRF, withLoggedUser, translate)(BillingForm);
